// src/features/fx-info/components/FxCotPositioningChart.tsx
//
// Pillar 4 — COT Positioning Extremes (the 4th synchronized pane).
// Two vertically-stacked panes, both wired to syncId="fx-master-sync" so their
// crosshair snaps to the same day as the Price / Drawdown / Frac-Diff panes:
//   1. Raw CFTC net positioning — Commercials (hedgers) vs Non-Commercials
//      (speculators), in contracts.
//   2. The 3-year rolling Z-score of speculator net positioning. Reference
//      bands at +2.0 / -2.0 flag statistically overcrowded (or over-sold)
//      trades — a classic mean-reversion / contrarian signal.
//
// Consumes the SAME FxTimeSeriesPoint[] array as FxTimeSeriesChart so the
// X-axes stay perfectly aligned. COT data is weekly, so the daily series
// carries nulls between prints — connectNulls bridges them into a clean line.

import { useMemo } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { FxTimeSeriesPoint } from '../api';

interface Props {
    data: FxTimeSeriesPoint[];
}

const formatContracts = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}k`;
    return `${sign}${abs.toFixed(0)}`;
};

export const FxCotPositioningChart = ({ data }: Props) => {
    const chartData = useMemo(
        () =>
            data.map((d) => ({
                ...d,
                displayDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
                axisDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                }),
            })),
        [data],
    );

    if (!data.length) return null;

    const tooltipStyle = {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        color: '#f8fafc',
        fontSize: '13px',
    };

    // Latest non-null Z-score, for the headline extremes badge.
    const latestZ = (() => {
        for (let i = data.length - 1; i >= 0; i -= 1) {
            const z = data[i].positioning_z_score_3yr;
            if (z != null && Number.isFinite(z)) return z;
        }
        return null;
    })();

    const zBadge = (() => {
        if (latestZ == null) return null;
        if (latestZ >= 2)
            return {
                label: 'Overcrowded Long',
                cls: 'text-red-700 dark:text-red-400 bg-red-500/10 border-red-500/30',
            };
        if (latestZ <= -2)
            return {
                label: 'Overcrowded Short',
                cls: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
            };
        return {
            label: 'Neutral',
            cls: 'text-slate-600 dark:text-slate-300 bg-slate-500/10 border-slate-500/30',
        };
    })();

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                        COT Positioning Extremes
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Commercial (hedger) vs Speculator net positioning and
                        the 3-year rolling Z-score. |Z| &gt; 2.0 flags an
                        overcrowded trade.
                    </p>
                </div>
                {zBadge && latestZ != null && (
                    <span
                        className={`shrink-0 rounded-md border px-2.5 py-1 text-xs font-bold ${zBadge.cls}`}
                    >
                        Z = {latestZ.toFixed(2)} · {zBadge.label}
                    </span>
                )}
            </div>

            {/* Top Pane: raw net positioning (Commercials vs Speculators) */}
            <div className="w-full h-[220px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4">
                    Net Positioning (Contracts)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="axisDate"
                            minTickGap={50}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={formatContracts}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number, name: string) => [
                                formatContracts(val),
                                name,
                            ]}
                        />
                        <ReferenceLine
                            y={0}
                            stroke="#475569"
                            strokeDasharray="3 3"
                        />
                        <Line
                            type="monotone"
                            dataKey="net_commercial"
                            name="Commercials"
                            stroke="#0ea5e9"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                        <Line
                            type="monotone"
                            dataKey="net_noncomm"
                            name="Speculators"
                            stroke="#10b981"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Bottom Pane: 3-year rolling Z-score with overcrowding bands */}
            <div className="w-full h-[160px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4">
                    Speculator Positioning Z-Score (3Y)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis dataKey="axisDate" hide />
                        <YAxis
                            domain={[-3, 3]}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [
                                val.toFixed(2),
                                'Z-Score',
                            ]}
                        />
                        {/* Overcrowded long / short bands */}
                        <ReferenceArea
                            y1={2}
                            y2={3}
                            fill="#ef4444"
                            fillOpacity={0.08}
                        />
                        <ReferenceArea
                            y1={-3}
                            y2={-2}
                            fill="#10b981"
                            fillOpacity={0.08}
                        />
                        <ReferenceLine
                            y={2}
                            stroke="#ef4444"
                            strokeDasharray="4 4"
                            strokeOpacity={0.7}
                        />
                        <ReferenceLine
                            y={-2}
                            stroke="#10b981"
                            strokeDasharray="4 4"
                            strokeOpacity={0.7}
                        />
                        <ReferenceLine y={0} stroke="#475569" />
                        <Line
                            type="monotone"
                            dataKey="positioning_z_score_3yr"
                            name="Z-Score"
                            stroke="#6366f1"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default FxCotPositioningChart;
